import { isObject } from './isObject';
import { SGWTConnectError } from './SGWTConnectError';
import {
  ERR_FAILED_TO_FETCH_OPENID_METADATA,
  ERR_FAILED_TO_PARSE_OPENID_METADATA,
  ERR_FAILED_TO_VALIDATE_OPENID_METADATA,
} from './SGWTConnectErrorList';

/**
 * See https://openid.net/specs/openid-connect-session-1_0.html#OPMetadata
 * See https://openid.net/specs/openid-connect-discovery-1_0.html#ProviderMetadata
 */
export interface ISGWTConnectOpenIDMetadata {
  /**
   * URL of an OpenID Provider's iframe for session state information.
   */
  check_session_iframe: string;

  /**
   * URL at the OP to which an RP can perform a redirect to request
   * that the End-User be logged out at the OP.
   */
  end_session_endpoint: string;

  /**
   * Other ProviderMetadata properties may be defined here.
   * See https://openid.net/specs/openid-connect-discovery-1_0.html#ProviderMetadata
   */
  [key: string]: string;
}

/**
 * Async fetcher, parser, and validator of OpenID Metadata.
 * @param callback Called with OpenID Metadata or Error on response.
 */
export type ISGWTConnectFetchOpenIDMetadata = (callback: (config: Error | ISGWTConnectOpenIDMetadata) => void) => void;

/**
 * Generates a function that upon invocation asynchronously
 * fetches OpenID Metadata from the OpenID Provider.
 * @param authorization_endpoint OpenID Provider's endpoint URL.
 */
export function createOpenIDMetadataFetcher(authorization_endpoint: string): ISGWTConnectFetchOpenIDMetadata {
  return function fetchOpenIDMetadata(callback) {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${authorization_endpoint}/oauth2/.well-known/openid-configuration`);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.addEventListener('load', function onOpenIDMetadataLoad() {
      if (xhr.status === 200) {
        let openIDMetadata;
        try {
          openIDMetadata = JSON.parse(xhr.responseText);
        } catch {
          callback(new SGWTConnectError(ERR_FAILED_TO_PARSE_OPENID_METADATA));
          return;
        }
        if (isValidOpenIDMetadata(openIDMetadata)) {
          callback(openIDMetadata);
        } else {
          callback(new SGWTConnectError(ERR_FAILED_TO_VALIDATE_OPENID_METADATA));
        }
      } else {
        callback(new SGWTConnectError(ERR_FAILED_TO_FETCH_OPENID_METADATA));
      }
    });
    xhr.send();
  };
}

/**
 * Determines whether the given OpenID Configuration is valid.
 * @param openIDMetadata JSON-parsed value obtained from the Discovery API.
 */
function isValidOpenIDMetadata(openIDMetadata: any): openIDMetadata is ISGWTConnectOpenIDMetadata {
  return (
    isObject(openIDMetadata) &&
    typeof openIDMetadata.check_session_iframe === 'string' &&
    typeof openIDMetadata.end_session_endpoint === 'string'
  );
}
